import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';

import { LectureSearchInput } from '@/components/ui/features/library/LectureSearchInput/LectureSearchInput';
import { LibraryFiltersPageContent } from '@/components/ui/features/library/LibraryFiltersPageContent/LibraryFiltersPageContent';
import { LibraryHeader } from '@/components/ui/features/library/LibraryHeader/LibraryHeader';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';
import { accessFrom } from '@/constants/custom-request-headers';
import { AccessFrom } from '@/enums/AccessFrom';
import { LectureSort } from '@/enums/LectureSort';
import { LibraryFilter } from '@/enums/LibraryFilter';
import { StringParam } from '@/hooks/useQueryParams';
import { fetchOmnisearchLectureAccessPermissionsSideload } from '@/queries/lecture-access-permissions';
import { queryOmnisearchLectureInfinite } from '@/queries/omnisearch-thin-lectures';
import { generateStore } from '@/utils/generateStore';
import { Hydrate } from '@/utils/swr/Hydrate';
import { urlQueryStringValidate } from '@/utils/url-query-string';

const SearchLibraryPage: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  fallback,
  accessFromLocation,
}) => {
  return (
    <Hydrate fallback={fallback}>
      <Layout accessFrom={accessFromLocation} navigationVariant="default">
        <LibraryHeader />
        <LectureSearchInput />
        <LibraryFiltersPageContent />
      </Layout>
    </Hydrate>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });
  const { query, sort = LectureSort.Relevance } = urlQueryStringValidate<{ query: string; sort: string }>(
    context.query,
    {
      query: StringParam,
      sort: StringParam,
    }
  );

  const filters = {};

  Object.values(LibraryFilter).forEach((filter) => {
    if (context.query[filter]) {
      filters[filter] = context.query[filter];
    }
  });

  await store.fetchResourcesInfinite(
    queryOmnisearchLectureInfinite({
      filters: {
        library: 'true',
        scope: 'published',
        query: query,
        ...filters,
      },
      sort: sort === LectureSort.Relevance && !query ? `-${LectureSort.HeldAt}` : sort,
      pageSize: 12,
    }),
    {
      sideload: fetchOmnisearchLectureAccessPermissionsSideload(store),
    }
  );

  return {
    props: {
      fallback: store.fallback,
      accessFromLocation: context.req.headers[accessFrom] || AccessFrom.Core,
    },
  };
};

export default SearchLibraryPage;
